import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BrowserEnvironmentAdapter,
  FetchNetworkAdapter,
  LocalStorageAdapter,
  TV2M_STORAGE_KEYS,
  Tv2mSdk,
  WebPushAdapter,
  needsIosInstallForPush,
} from '@apm/tv2m-client-sdk';
import type {
  DeferredLinkResult,
  PushSubscribeResult,
  Tv2mInitResult,
  Tv2mLinkContext,
  Tv2mRoute,
} from '@apm/tv2m-client-sdk';
import type {
  BloomDeviceRegisterResponse,
  BloomRuntimeConfig,
  WebPushSubscription,
} from '@apm/contracts';
import type { Locale } from '@apm/i18n';
import { useLocale } from './i18n/LocaleProvider.js';
import { APP_ID, config } from './config.js';
import {
  nativeMeans,
  registerBloomDevice,
  sameRegistration,
  unsubscribeBloomDevice,
} from './bloom-device.js';
import { getNativeBridge, type NativeBridge } from './native/bridge.js';
import { settleWithin } from './native/settle.js';
import { clearSubscribedKey, readSubscribedKey, writeSubscribedKey } from './push/key-store.js';
import {
  planReconcile,
  subscribeWithOwner,
  subscriptionOwner,
  type PushMeans,
} from './push/subscription.js';
import { registerServiceWorker } from './sw-registration.js';

/**
 * SDK 를 세우고 화면이 읽을 상태 한 벌을 만든다.
 *
 * ── 첫 실행 / Deferred 복원 판정 (`docs/09` T7 의 R6) ──────────
 *
 * ```
 * 앱 진입:
 *   저장소에 deviceId 가 없다  →  "첫 실행"
 *       1) deviceId 생성 → POST /v1/device/register        ← SDK 의 initialize 가 한다
 *       2) URL 의 ?ref=tv2m_qr_{token} 을 sessionToken 으로 보관  ← SDK 의 route() 가 한다
 *       3) GET /v1/deferred-link/check
 *       4) found 면 상품 화면으로
 *   있다  →  평상 실행. Deep Link 파라미터가 있으면 그대로 라우팅
 * ```
 *
 * **네이티브 포팅에서 이 판정은 "커스텀 스킴 실행 실패 → 스토어 → 설치 후 첫 실행" 으로
 * 대체된다** (`docs/09` T7 이 이 대응 관계를 여기 남기라고 지목한 자리다). 바뀌는 것은
 * "첫 실행을 무엇으로 아는가" 뿐이고, 그 뒤의 세 단계는 그대로다 — 그래서 판정만
 * `Tv2mSdk.isFirstRun()` 뒤에 숨겨 두었다.
 *
 * ── 코어에 타이머가 없다 ──────────────────────────────────────
 *
 * `Tv2mSdk` 는 heartbeat 를 스스로 반복하지 않는다 (`lib: ES2022` 에 `setInterval` 이
 * 없다, 절대 규칙 13). **주기 실행은 화면의 몫이고 그 자리가 여기다** — 빠뜨리면 기기가
 * 2시간 뒤 `idle`, 24시간 뒤 `dormant` 로 늙어 Tier 2 대상에서 빠지고, 증상은
 * "알림이 안 온다" 뿐이다 (T6 이 미상환으로 남긴 자리).
 */
export interface Tv2mState {
  /** 초기화가 끝났는가. 끝나기 전에는 화면이 아무 판단도 하면 안 된다 */
  ready: boolean;
  init: Tv2mInitResult | null;
  /** 이번 진입의 라우팅 결정 (Deep Link / 설치 ref / 없음) */
  route: Tv2mRoute;
  /** 상품 컨텍스트. Deep Link 든 Deferred 복원이든 여기로 모인다 */
  context: Tv2mLinkContext | null;
  /** 첫 실행에서 Deferred 를 조회했는가. `null` 이면 조회하지 않았다 */
  deferred: DeferredLinkResult | null;
  /**
   * iOS 인데 홈 화면에 추가되지 않아 Push 가 원천 불가능한가 (WB-08).
   *
   * **셸 안에서는 언제나 거짓이다** (v12, `docs/29` §5-3). 그 판정은 UA 에 `iPhone` 이
   * 있고 `navigator.standalone` 이 참이 아닌 것을 보는데 **WKWebView 가 정확히 그
   * 상태다** — 그대로 두면 네이티브 앱에 「홈 화면에 추가하세요」가 뜨고, 그것은 거짓말인
   * 데다 **사용자가 할 수 있는 일이 없는 안내**다.
   */
  needsIosInstall: boolean;
  /**
   * BLOOM 기기 등록 결과. **federated 에서만 채워진다** — 계정 연결과 구독의 주인이
   * 그 모드에서 BLOOM Server 이기 때문이다. 실패하면 `null` 이고 화면이 그 사실을 말한다.
   */
  device: BloomDeviceRegisterResponse | null;
  /**
   * 셸에게 APNs 등록을 물은 결과. 브리지가 없으면 `null` (웹·PWA 경로).
   *
   * **`?debug=1` 이 이것을 보여 준다.** 없으면 「알림이 안 온다」가 났을 때 「권한이 없다」
   * 와 「셸이 답하지 않았다」가 화면에서 구분되지 않고, iOS 에는 CDP 가 붙지 않아
   * (`docs/29` §10) 그것을 물을 창이 Safari 웹 인스펙터뿐이다.
   */
  nativePush: 'ready' | 'missing' | 'timeout' | null;
  /**
   * 마지막 등록·기동 실패의 이유. 성공했거나 아직 시도하지 않았으면 `null`.
   *
   * **이 값이 없어서 데모의 원인을 못 갈랐다.** 등록이 실패하면 `device` 가 `null` 이고
   * 화면은 「알림을 켜 주세요」를 띄우는데, 그것은 **사용자가 권한을 안 준 것과
   * 서버에 요청이 닿지 않은 것을 같은 말로 덮는다.** `?debug=1` 이 이것을 보여 준다.
   */
  registerError: string | null;
}

export interface Tv2mApi extends Tv2mState {
  /**
   * 이 기기에 알림을 보낼 수단이 있는가. **모드에 따라 진실의 출처가 다르다** —
   * federated 는 BLOOM Server 의 등록 응답이고 delegated 는 SDK 다. 화면은 어느 쪽인지 알
   * 필요가 없다 (절대 규칙 17 이 프런트에서 성립하는 방식이다).
   */
  pushSubscribed: boolean;
  /** 이 기기가 계정에 묶였는가. Tier 1 의 전제다 */
  linkedToAccount: boolean;
  /** 알림 권한 요청 + 구독. **사용자 제스처 안에서만 부를 것** (WB-07) */
  enablePush: () => Promise<PushSubscribeResult>;
  trackEvent: Tv2mSdk['trackEvent'];
  flushEvents: () => Promise<void>;
  sdk: Tv2mSdk | null;
}

const INITIAL: Tv2mState = {
  ready: false,
  init: null,
  route: { kind: 'none' },
  context: null,
  deferred: null,
  needsIosInstall: false,
  device: null,
  nativePush: null,
  registerError: null,
};

/**
 * @param runtime `GET /v1/bloom/config` 가 준 설정. **부팅 순서상 이것이 먼저다**
 *   (`docs/14` B8) — 모드·VAPID 키·TV2M 주소를 모른 채 SDK 를 세울 수 없다.
 */
export function useTv2m(runtime: BloomRuntimeConfig, token: string | null): Tv2mApi {
  const [state, setState] = useState<Tv2mState>(INITIAL);
  /**
   * 화면 언어 (v9). **Push 알림 문구가 이 값으로 갈린다** — 서버는 브라우저의 언어
   * 설정을 모르고 Service Worker 도 앱의 저장소를 읽을 수 없어, 알려 주는 통로가
   * 기기 등록 하나다 (`docs/22` §8).
   */
  const { locale } = useLocale();
  const sdkRef = useRef<Tv2mSdk | null>(null);
  /**
   * 권한 요청은 **어댑터에게 직접 한다.** 코어는 `requestPermission()` 을 부르지 않으므로
   * (WB-07 — 어떤 호출이 사용자 제스처 안에 있는지 코어는 알 수 없다) 그것을 호출할 수
   * 있는 것은 어댑터를 쥐고 있는 이 화면뿐이다.
   */
  const pushRef = useRef<WebPushAdapter | null>(null);

  /*
   * ── 재등록이 읽는 최신값 ────────────────────────────────────
   *
   * 부팅 뒤에 일어나는 등록(늦은 수단·자가 치유)은 **부팅 시점의 클로저를 쓰면 안 된다.**
   * `locale` 을 되돌리는 경로가 실재하고(위 `Startup.late` 주석) 그 증상이 조용하다.
   * ref 로 읽으면 「지금 화면이 무엇인가」가 언제나 답이다.
   */
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const tokenRef = useRef(token);
  tokenRef.current = token;
  /** 마지막으로 확보한 알림 수단. 자가 치유가 이것을 다시 싣는다 */
  const meansRef = useRef<PushMeans | null>(null);
  /** 마지막 등록 실패의 이유. **`?debug=1` 이 이것을 보여 준다** (아래 `syncDevice`) */
  const registerErrorRef = useRef<string | null>(null);

  /**
   * BLOOM Server 에 기기를 등록한다. **부팅 이후의 모든 등록이 이 한 곳을 지난다.**
   *
   * `locale`·`token` 을 ref 에서 읽으므로 언제 불려도 지금 화면의 값이 실린다.
   * `means` 를 주지 않으면 마지막으로 확보한 것을 다시 싣는다 — **수단 없이 등록하면
   * 행은 생겨도 도달 불가라**(서버의 `hasPushMeans`) 증상이 그대로다.
   *
   * **실패 이유를 남긴다.** 원래는 `catch { return null }` 이라 이유가 어디에도 남지
   * 않았고, 화면에는 「알림을 켜 주세요」로만 보였다 — 서버에 요청이 닿지 않은 것과
   * 사용자가 권한을 안 준 것이 **화면에서 구분되지 않는다.** 2026-08-12 데모에서
   * iPhone 이 그 상태였다.
   */
  const syncDevice = useCallback(
    async (means?: PushMeans | null): Promise<BloomDeviceRegisterResponse | null> => {
      const sdk = sdkRef.current;
      if (sdk === null) return null;
      // delegated 는 구독의 주인이 TV2M 이라 BLOOM 에 등록할 것이 없다.
      if (subscriptionOwner(runtime.mode) !== 'fast') return null;

      if (means !== undefined && means !== null) meansRef.current = means;
      const carried = means === undefined ? meansRef.current : means;

      try {
        const device = await registerBloomDevice({
          serverUrl: config.serverUrl,
          token: tokenRef.current,
          deviceId: sdk.getDeviceId(),
          appId: APP_ID,
          platform: getNativeBridge()?.platform ?? 'web',
          locale: localeRef.current,
          ...(carried ? { means: carried } : {}),
        });
        registerErrorRef.current = null;
        // **바뀐 것이 없으면 `prev` 를 그대로 돌려준다.** heartbeat 마다 불리므로(아래)
        // 새 객체를 만들면 5분마다 시청 화면이 이유 없이 다시 그려진다.
        setState((prev) =>
          sameRegistration(prev.device, device) && prev.registerError === null
            ? prev
            : { ...prev, device, registerError: null },
        );
        return device;
      } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);
        registerErrorRef.current = reason;
        setState((prev) =>
          prev.registerError === reason ? prev : { ...prev, registerError: reason },
        );
        return null;
      }
    },
    [runtime.mode],
  );

  useEffect(() => {
    // 정리에서 깃발을 내려 늦게 도착한 결과를 버린다. **기동 자체는 이미 한 번뿐이다**
    // (`startup()` 의 단일 비행) — 이 깃발이 막는 것은 언마운트된 컴포넌트의 `setState` 다.
    let alive = true;

    void startup(runtime, token, locale)
      .then((result) => {
        if (!alive) return;
        sdkRef.current = result.sdk;
        pushRef.current = result.push;
        meansRef.current = result.means;
        setState(result.state);

        /*
         * **늦게 온 수단으로 다시 등록한다.**
         *
         * 상한만 두면 기기 행은 생기지만 **토큰이 없어 증상이 그대로다.** 이것이 상한의
         * 값어치 전부이고, 없으면 다음 heartbeat 의 자가 치유까지 기다려야 한다.
         */
        void result.late?.then((delivered) => {
          if (!alive || delivered === null) return;
          void syncDevice(delivered);
        });
      })
      /*
       * **기동이 던져도 앱이 멈추지 않게 한다.**
       *
       * `startupPromise` 는 **거절된 약속도 기억하므로**(단일 비행) 리마운트해도
       * 재시도가 없고 `state.ready` 가 영영 거짓으로 남는다 — 증상이 이번 결함과
       * 구분되지 않는다. 이유를 적어 두면 `?debug=1` 이 그것을 말한다.
       */
      .catch((error: unknown) => {
        const reason = `기동 실패: ${error instanceof Error ? error.message : String(error)}`;
        registerErrorRef.current = reason;
        if (alive) setState((prev) => ({ ...prev, registerError: reason }));
      });

    return () => {
      alive = false;
    };
    // 의존성이 비어 있다. 설정은 부팅에 한 번 받고 바뀌지 않으며, 기동은 어차피
    // 단일 비행이라 효과가 다시 돌아도 같은 결과를 받는다. **`locale` 도 넣지 않는다** —
    // 기동은 한 번뿐이고, 언어가 바뀐 뒤의 반영은 아래 재등록 효과가 맡는다.
  }, []);

  // ── heartbeat ────────────────────────────────────────────────
  //
  // 주기는 서버가 정한 값(`nextHeartbeatInSeconds`)이다. 숫자를 화면에 두면 서버가
  // 정책을 바꿔도 앱이 따라가지 않는다.
  const intervalSec = state.init?.nextHeartbeatInSeconds ?? null;
  useEffect(() => {
    if (intervalSec === null) return;

    const beat = () => {
      // **화면이 보이는지로 `appState` 를 정한다.** 서버는 이 값으로 활성도를 갱신하고
      // (T2) Tier 2 가 그것으로 대상을 고른다. 늘 `foreground` 로 보내면 서랍 속 폰이
      // 영원히 깨어 있는 것으로 보인다.
      const appState = document.visibilityState === 'visible' ? 'foreground' : 'background';
      void sdkRef.current?.heartbeat(appState);

      /*
       * **beat 마다 다시 등록한다** — 서버에 행이 있는지 앱은 알 수 없기 때문이다.
       *
       * BLOOM 등록은 페이지 한 번 열림에 시도 한 번이고, 실패하면 그 탭이 살아 있는
       * 내내 복구가 없었다. 2026-08-12 데모에서 iPhone 이 몇 분 동안 열려 있었는데도
       * 행이 생기지 않았고, **재시도가 하나만 있었어도 복구됐다.**
       *
       * **SDK 에 같은 선례가 있다** — TV2M heartbeat 는 404 를 받으면 스스로 재등록한다
       * (`tv2m-client.ts`: 「저장소에 deviceId 를 든 앱이 영원히 404 를 두드린다」).
       * BLOOM 쪽에만 그 대응물이 없었다.
       *
       * ── 왜 조건이 없는가 (2026-08-15) ─────────────────────────
       *
       * 원래는 `deviceRef.current === null` 일 때만 했다. 그 값은 **「우리가 등록에 성공한
       * 적이 있는가」이지 「지금 서버에 행이 있는가」가 아니다** — 성공하면 채워진 채로
       * 남고, 그 뒤 서버에서 행이 사라져도 앱은 알 방법이 없다.
       *
       * 실제로 그렇게 됐다. 폰이 정상 등록한 뒤 하네스가 기기 테이블을 비웠고, 셸은
       * 백그라운드에서 돌아올 때 WebView 를 다시 로드하지 않으므로(`docs/29` §6.1-1)
       * 그 기억이 18시간을 살아남았다. **앱은 「BLOOM 등록: ok · 발송 가능 true」라고
       * 말하는데 서버에는 행이 없었다** — 그동안 그 폰은 발송 대상에서 통째로 빠져
       * `PushDelivery` 에 행이 **아예 없었고**(`docs/11` §8 의 그 줄), [알림 켜기] 를
       * 껐다 켜도 소용이 없었다. 앱을 완전히 종료해야 풀렸다.
       *
       * `POST /v1/bloom/device` 는 upsert 라 **묻는 것과 고치는 것이 한 번에 끝난다.**
       * 조회 경로를 따로 만들지 않는 이유가 이것이다 — TV2M 은 서버가 404 로 알려
       * 주지만 BLOOM 에는 heartbeat 가 없어 **묻는 쪽이 우리다.**
       *
       * **조건을 다시 달지 말 것.** 앱이 가진 어떤 값도 서버의 지금을 대변하지 못한다.
       * 헛렌더가 걱정이면 그것은 `syncDevice` 가 `sameRegistration` 으로 이미 막는다.
       */
      void syncDevice();
    };

    beat();
    const timer = window.setInterval(beat, intervalSec * 1000);

    // 화면으로 돌아온 순간에도 한 번 보낸다. 백그라운드에서는 타이머가 억제되므로
    // (모바일 브라우저가 특히 그렇다) 이것이 없으면 앱을 다시 열어도 서버는 한참
    // 뒤에야 이 폰이 여기 있는 줄 안다.
    const onVisible = () => {
      if (document.visibilityState === 'visible') beat();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // `syncDevice` 는 `runtime.mode` 에만 걸려 있고 그것은 부팅에 한 번 받는 값이다.
    // deps 에 넣으면 타이머가 재생성될 자리가 하나 늘 뿐이다.
  }, [intervalSec, syncDevice]);

  /**
   * 언어를 바꾸면 기기를 **다시 등록한다** (v9, `docs/22` §8).
   *
   * **절대 규칙 19 를 건드리지 않는다** — `unsubscribe()` 를 부르지 않고 구독도
   * `subscribedWithKey` 도 싣지 않는다. 서버는 실려 오지 않은 구독을 지우지 않으므로
   * (`bloom-device.service.ts`) `locale` 한 칸만 갱신된다.
   *
   * **재생을 멈추지 않는다** (절대 규칙 25). 이것은 네트워크 호출 하나이고 리시버를
   * 건드리지 않는다 — 언어 전환이 `location.reload()` 가 아닌 이유와 같은 자리다.
   *
   * 첫 기동의 등록은 위 효과가 이미 같은 값으로 했다. 여기서 한 번 더 보내는 것을
   * 막으려고 **마지막으로 보낸 값을 기억한다** — 없으면 부팅마다 등록이 두 번이다.
   */
  const sentLocale = useRef<Locale | null>(null);
  useEffect(() => {
    if (!state.ready || state.init === null) return;
    if (subscriptionOwner(runtime.mode) !== 'fast') return;
    if (sentLocale.current === null) {
      // 기동이 실은 값이다. 다시 보내지 않는다.
      sentLocale.current = locale;
      return;
    }
    if (sentLocale.current === locale) return;
    sentLocale.current = locale;

    // 실패해도 던지지 않는다. 알림 문구가 옛 언어로 나갈 뿐이고, **시청·구매는 전부
    // 동작해야 한다** (등록 실패를 다루는 방침 그대로다).
    void registerBloomDevice({
      serverUrl: config.serverUrl,
      token,
      deviceId: state.init.deviceId,
      appId: APP_ID,
      // 셸이면 `ios` 다. 이 재등록이 `platform` 을 `web` 으로 되돌리면 **언어를 바꾼
      // 기기만 기록이 웹으로 바뀐다** — 발송에는 영향이 없어 조용하다.
      platform: getNativeBridge()?.platform ?? 'web',
      locale,
    }).catch(() => null);
  }, [locale, state.ready, state.init, runtime.mode, token]);

  // 남은 이벤트를 떠나기 전에 보낸다. `pagehide` 를 쓴다 — `beforeunload` 는 모바일
  // 사파리에서 발화하지 않고, `unload` 는 bfcache 를 깨뜨린다.
  useEffect(() => {
    const flush = () => void sdkRef.current?.flushEvents();
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  /**
   * 알림 권한 요청 + 구독 (WB-07).
   *
   * **요청과 구독이 한 함수 안에 있다.** 브라우저는 사용자 제스처 직후가 아닌 권한
   * 요청을 거부하거나 조용히 무시하는데, 화면이 두 번에 나눠 부르면 두 번째가 제스처
   * 밖으로 밀려난다. 코어가 `requestPermission()` 을 부르지 않는 이유가 이것이고
   * (`PushAdapter` 주석), 그 대신 부르는 자리가 여기다.
   */
  const enablePush = useCallback(async (): Promise<PushSubscribeResult> => {
    const sdk = sdkRef.current;
    const push = pushRef.current;
    if (sdk === null || push === null) {
      return { subscribed: false, permission: 'unsupported', reason: 'unsupported' };
    }

    const native = getNativeBridge();

    // 권한을 먼저 묻고, 떨어졌으면 구독을 붙인다. 거부돼도 결과가
    // `reason: 'permission_denied'` 로 오므로 여기서 분기하지 않는다 — 결과의 모양이
    // 하나여야 화면이 안내를 한 곳에서 고른다.
    //
    // **셸 안에서는 부르지 않는다** (v12). WKWebView 에 `Notification` 이 없어 뜻이 없고,
    // 네이티브 권한은 `requestPush()` 안에서 `UNUserNotificationCenter` 가 묻는다 —
    // 여기서 한 번 더 물으면 **거부 이력만 웹 쪽에 남고 아무 일도 일어나지 않는다.**
    if (native === null) await push.requestPermission();

    const result = await subscribeWithOwner({
      mode: runtime.mode,
      vapidPublicKey: runtime.vapidPublicKey,
      push,
      sdk,
      // 셸 안이면 수단이 APNs 다 (규칙 38). **주인은 그대로 BLOOM Server 다.**
      requestNativePush: native === null ? null : () => native.requestPush(),
      /*
       * **등록은 `syncDevice` 한 곳을 지난다.** 여기서 따로 부르면 `locale` 이 이
       * 콜백의 클로저에서 오는데, 그 값은 `enablePush` 의 deps 에 없어 **언어를 바꾼
       * 뒤 [알림 켜기] 를 누르면 옛 언어로 등록된다.** `eslint.config.mjs` 에
       * `react-hooks` 플러그인이 없어 아무도 잡아 주지 않는 자리였다.
       */
      registerWithBloom: async (means) => {
        const device = await syncDevice(means);
        // **던져야 `subscribeWithOwner` 가 `server_error` 로 갈린다.** `syncDevice` 는
        // 자가 치유가 그 위에 서므로 던지지 않고 이유만 적어 둔다 — 여기서 되살린다.
        if (device === null) throw new Error(registerErrorRef.current ?? '기기 등록 실패');
        return device;
      },
    });

    // **구독에 쓴 키를 적어 둔다** (WB-14). 브라우저는 어느 키로 만들어졌는지 알려 주지
    // 않으므로, 다음 부팅이 대조할 근거는 이 한 줄뿐이다 (절대 규칙 19).
    //
    // **네이티브에는 쓰지 않는다** — APNs 에 VAPID 키가 없다. 대응하는 대조는 토큰이고
    // (D11) 그것은 셸이 들고 있어 적어 둘 필요가 없다.
    if (result.subscribed && native === null) {
      writeSubscribedKey(runtime.vapidPublicKey, subscriptionOwner(runtime.mode));
    }

    // 권한·구독 상태가 바뀌었으므로 다시 읽는다. **추정하지 않는다** — 권한이 `granted`
    // 라도 구독이 실패할 수 있고, 그때 "알림이 켜져 있습니다" 를 띄우면 아무것도 오지
    // 않는 이유가 어디에도 남지 않는다.
    // `device` 는 `syncDevice` 가 이미 넣었다 — 여기서 다시 쓰면 실패한 등록이 앞의
    // 성공을 덮을 수 있다.
    setState((prev) => ({ ...prev, init: sdk.getInitResult() }));
    return result;
  }, [runtime.mode, runtime.vapidPublicKey, syncDevice]);

  const trackEvent = useCallback<Tv2mSdk['trackEvent']>((type, detail) => {
    sdkRef.current?.trackEvent(type, detail);
  }, []);

  const flushEvents = useCallback(async (): Promise<void> => {
    await sdkRef.current?.flushEvents();
  }, []);

  /*
   * **진실의 출처가 모드에 따라 다르다.** federated 에서 구독을 가진 것은 BLOOM Server 이므로
   * SDK 는 그 사실을 모른다 — SDK 의 `pushSubscribed` 는 그 모드에서 언제나 거짓이다.
   * 화면이 그것을 그대로 읽으면 **알림이 켜져 있는데 「알림을 켜 주세요」가 남는다.**
   */
  const pushSubscribed =
    subscriptionOwner(runtime.mode) === 'fast'
      ? (state.device?.pushReady ?? false)
      : (state.init?.pushSubscribed ?? false);

  return {
    ...state,
    pushSubscribed,
    linkedToAccount: state.device?.linkedToAccount ?? false,
    enablePush,
    trackEvent,
    flushEvents,
    sdk: sdkRef.current,
  };
}

interface Startup {
  sdk: Tv2mSdk;
  push: WebPushAdapter;
  state: Tv2mState;
  /**
   * 부팅이 확보한 알림 수단. **자가 치유가 이것을 다시 싣는다** — 수단 없이 재등록하면
   * 행은 생겨도 도달 불가라 증상이 그대로다.
   */
  means: PushMeans | null;
  /**
   * 상한을 넘긴 셸 조회. `null` 이면 제때 답했거나 웹 경로다.
   *
   * **`bootstrap` 안에서 처리하지 않는다.** 이 함수는 `locale`·`token` 을 인자로 받아
   * 클로저에 가두는데, 늦게 도착한 값으로 그 시점의 언어를 다시 실어 보내면
   * **사용자가 그 사이에 바꾼 언어를 되돌린다** — 서버가 `locale` 을 덮어쓰고
   * (`bloom-device.service.ts`) 언어 효과는 이미 보냈다고 여겨 다시 보내지 않는다.
   * 증상은 「알림 문구만 옛 언어로 나간다」 하나다. 훅이 ref 로 최신값을 읽는다.
   */
  late: Promise<PushMeans | null> | null;
}

/**
 * 페이지 한 번 열림 = 기동 한 번. **모듈 스코프에 약속을 붙들어 단일 비행으로 만든다.**
 *
 * ── 왜 효과 안의 깃발로는 부족한가 ────────────────────────────
 *
 * `StrictMode` 는 효과를 두 번 돌린다. 깃발(`alive`)은 **결과를 버릴** 뿐 두 번째 실행을
 * 막지 못하는데, 이 기동에는 되돌릴 수 없는 부작용이 셋 있다.
 *
 * 1. **첫 실행 판정이 소모된다.** `Tv2mSdk` 는 `tv2m.launched` 를 보고 첫 실행을 정하고
 *    그 자리에서 값을 쓴다(T6 — "저장소만 보고 정해진다"). 두 번 돌면 두 번째는 언제나
 *    `firstRun: false` 이고, **그러면 Deferred 복원이 통째로 일어나지 않는다.**
 *    실기에서 실제로 이것을 봤다 — 새 프로필로 처음 연 화면이 `firstRun: false` 였다.
 * 2. `POST /v1/device/register` 가 두 번 나간다.
 * 3. `checkDeferredLink()` 가 두 번 나가고, **살아 있는 링크는 첫 호출에서 소비된다**
 *    (`consumedAt`, T5). 두 번째는 언제나 `found: false` 다.
 *
 * 셋 다 "결과를 버리면 되는" 종류가 아니다. 그래서 약속 자체를 하나로 둔다 —
 * 두 번째 호출은 첫 번째의 결과를 그대로 받는다. 페이지가 새로 열리면 모듈도 새로
 * 평가되므로 다음 방문에는 다시 한 번 돈다.
 *
 * **이것이 `StrictMode` 를 켠 대가이자 이유다** (`main.tsx`). 운영 빌드에서는 효과가 한
 * 번만 돌아 이 결함이 드러나지 않는데, 드러나지 않는다고 없는 것은 아니다 — 화면이
 * 리마운트되는 어떤 변경도 같은 증상을 만든다.
 */
let startupPromise: Promise<Startup> | null = null;

function startup(
  runtime: BloomRuntimeConfig,
  token: string | null,
  locale: Locale,
): Promise<Startup> {
  startupPromise ??= bootstrap(runtime, token, locale);
  return startupPromise;
}

async function bootstrap(
  runtime: BloomRuntimeConfig,
  token: string | null,
  locale: Locale,
): Promise<Startup> {
  const storage = new LocalStorageAdapter();

  // Service Worker 등록은 SDK 가 하지 않는다 (경로·스코프는 앱의 사정이다).
  // **`deviceId` 를 SDK 인스턴스가 아니라 저장소에서 읽는다** — 이 함수는
  // `Tv2mSdk.initialize()` **안에서** 처음 불릴 수 있고(이전 실행에서 권한이 이미
  // 떨어져 있으면 등록이 기존 구독을 함께 싣는다), 그 시점에는 인스턴스가 아직 없다.
  // SDK 는 등록보다 먼저 `deviceId` 를 저장소에 넣으므로 여기가 안전하다.
  const push = new WebPushAdapter(() =>
    registerServiceWorker({
      tv2mServerUrl: runtime.tv2mServerUrl,
      tv2mApiKey: runtime.tv2mApiKey,
      deviceId: storage.get(TV2M_STORAGE_KEYS.deviceId) ?? '',
    }),
  );

  const sdk = await Tv2mSdk.initialize(
    {
      serverUrl: runtime.tv2mServerUrl,
      apiKey: runtime.tv2mApiKey,
      appId: APP_ID,
      vapidPublicKey: runtime.vapidPublicKey,
      // WB-12. **SDK 가 이 값으로 하는 일은 하나뿐이다** — federated 에서 TV2M 등록에
      // 구독을 싣지 않는 것 (WB-13). 나머지는 두 모드가 완전히 같다.
      integrationMode: runtime.mode,
      enableDebugLog: config.debug,
    },
    {
      network: new FetchNetworkAdapter(runtime.tv2mApiKey),
      storage,
      push,
      env: new BrowserEnvironmentAdapter(),
    },
  );

  // **라우팅을 먼저 판정한다.** `route()` 가 설치 ref 를 저장소에 넣고, 그것을
  // `checkDeferredLink()` 가 소비한다 — 순서가 뒤집히면 QR 로 들어온 첫 실행이
  // 토큰 없이 지문만으로 조회하게 되고 문턱 2점을 넘지 못한다.
  const route = sdk.route(window.location.href);
  const context = route.kind === 'product' ? sdk.handleDeepLink(window.location.href) : null;

  let deferred: DeferredLinkResult | null = null;
  // **Deep Link 로 들어왔으면 조회하지 않는다.** 알고 있는 것을 두고 서버에 물어보러
  // 가면 왕복 한 번과 실패 가능성을 공짜로 얻고, 게다가 그 조회가 살아 있는 링크를
  // **소비해 버린다** (`consumedAt`, T5).
  if (context === null && sdk.isFirstRun()) {
    deferred = await sdk.checkDeferredLink();
  }

  const native = getNativeBridge();
  const { means, late, nativePush } = await reconcilePushMeans(runtime, sdk, push, native);
  const registered = await registerWithBloom(runtime, token, sdk, means, locale, native);

  return {
    sdk,
    push,
    means,
    late,
    state: {
      ready: true,
      init: sdk.getInitResult(),
      route,
      context: context ?? contextFromDeferred(deferred),
      deferred,
      // 셸 안에서는 A2HS 안내를 하지 않는다 (`Tv2mState.needsIosInstall` 주석).
      needsIosInstall: native === null && needsIosInstallForPush(),
      device: registered.device,
      nativePush,
      registerError: registered.error,
    },
  };
}

/**
 * BLOOM Server 에 기기를 등록한다 (federated 전용).
 *
 * **TV2M 등록과 별개로 한 번 더 등록한다.** 같은 `deviceId` 가 두 서버에 있어야 TV2M 이
 * 돌려준 `targetDeviceIds` 를 BLOOM Server 가 자기 구독으로 풀 수 있고, 그 연결이 federated
 * 발송의 전부다 (`docs/12` §6, `bloom-device.ts`).
 *
 * **로그인 상태가 여기서 기기에 새겨진다** — 토큰을 실어야 `BloomDevice.userId` 가 채워지고
 * 그것이 Tier 1 의 입력이다. 그래서 이 호출은 로그인 확인 뒤에 있어야 하고, 부팅 순서가
 * 그것을 보장한다 (`AuthGate` → `Shell` → 이 기동).
 *
 * 구독은 **`reconcileSubscription` 이 확인한 것만** 싣는다. 브라우저에 있다는 이유로
 * 그냥 실으면 어느 키로 만들어진 것인지 모르는 채 보내게 되고, 서버의 대조가
 * 무의미해진다 (절대 규칙 19).
 *
 * 실패해도 던지지 않는다. 등록이 안 돼도 시청·구매는 전부 동작해야 한다.
 *
 * **다만 이유는 남긴다.** 원래는 `catch { return null }` 이었고 그래서 「요청이 서버에
 * 닿지 않았다」가 어디에도 기록되지 않았다 — 화면에는 「알림을 켜 주세요」로만 보이고,
 * 그것은 사용자가 권한을 안 준 것과 **같은 말**이다. 2026-08-12 데모에서 그 둘을
 * 가르지 못했다.
 */
async function registerWithBloom(
  runtime: BloomRuntimeConfig,
  token: string | null,
  sdk: Tv2mSdk,
  means: PushMeans | null,
  locale: Locale,
  native: NativeBridge | null,
): Promise<{ device: BloomDeviceRegisterResponse | null; error: string | null }> {
  if (subscriptionOwner(runtime.mode) !== 'fast') return { device: null, error: null };

  try {
    const device = await registerBloomDevice({
      serverUrl: config.serverUrl,
      token,
      deviceId: sdk.getDeviceId(),
      appId: APP_ID,
      platform: native?.platform ?? 'web',
      locale,
      ...(means ? { means } : {}),
    });
    return { device, error: null };
  } catch (error) {
    return { device: null, error: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * 부팅 때 알림 수단을 확인한 결과.
 *
 * `late` 가 있으면 **상한을 넘겼다**는 뜻이고, 그 약속이 나중에 풀리면 호출부가 그
 * 수단으로 다시 등록한다. `means === null` 하나로는 「수단이 없다」와 「아직 안 왔다」가
 * 구분되지 않는다 — **그 구분이 이 필드의 전부다.**
 */
interface ReconciledMeans {
  means: PushMeans | null;
  late: Promise<PushMeans | null> | null;
  /** 진단용. 웹 경로면 `null` (`Tv2mState.nativePush`) */
  nativePush: 'ready' | 'missing' | 'timeout' | null;
}

/**
 * 수단 확인의 상한. **네트워크 관례(3초)보다 훨씬 짧다.**
 *
 * ── 왜 짧은가 ───────────────────────────────────────────────
 *
 * **따뜻한 경로가 정상이다.** 셸이 `didFinishLaunching` 에서 `current { _ in }` 으로
 * 토큰을 미리 데워 두므로(`AppDelegate.swift`) 웹이 물을 때는 캐시된 값이 **즉시**
 * 오고 드는 것은 브리지 한 홉이다. 차가운 경로는 1초로도 안 된다 — 500ms 안에 안
 * 왔다면 그것은 「느리다」가 아니라 「APNs 등록이 네트워크를 기다린다」이고 수 초에서
 * 영원까지다. **그 사이에 의미 있는 분포가 없다.**
 *
 * ── 늦어지면 무엇을 잃는가 ─────────────────────────────────
 *
 * 시청 화면은 아니다(`App.tsx` — 초기화를 기다리지 않는다). 대신 셋이 밀린다:
 * 첫 heartbeat · 설정 시트의 「준비 중」 · 언어 재등록(`state.ready` 가 조건이다).
 * **3초면 데모에서 사람이 알아챈다.**
 *
 * 넘겨도 잃는 것은 「POST 한 번 더」뿐이다 — 늦은 값이 오면 다시 등록하고, 그것도
 * 놓치면 heartbeat 의 자가 치유가 받는다.
 */
const MEANS_TIMEOUT_MS = 500;

/**
 * 부팅 때 알림 수단을 현재 상태와 맞춘다.
 *
 * **수단이 둘이라 갈래도 둘이다** (규칙 38). 재는 것은 다르지만 **막으려는 침묵은 같다** —
 * 어느 쪽이든 어긋나면 화면은 「알림 켜짐」인데 발송만 실패한다 (절대 규칙 19).
 *
 * ── 두 갈래 모두 상한을 씌운다 (2026-08-12) ─────────────────
 *
 * 여기서 기다리는 둘은 **안 풀리면 영영 안 풀린다** — 셸의 `getPushRegistration()` 과
 * `serviceWorker.ready` 다. 그러면 `bootstrap` 이 끝나지 않아 **기기 등록·heartbeat·
 * 전환 이벤트가 함께 죽는데**, 시청 화면은 초기화를 기다리지 않으므로 **화면만 멀쩡하다.**
 * 데모에서 iPhone 이 그 상태였고 증상은 「그 폰만 알림이 안 온다」 하나였다.
 *
 * | | 무엇이 갈리나 | 어떻게 대조하나 |
 * |---|---|---|
 * | 웹 | VAPID 키·모드 | 적어 둔 키와 서버 키 (`planReconcile`) |
 * | **네이티브** | **APNs 토큰** (재설치·기기 복원) | **셸에게 현재 토큰을 다시 묻는다** (D11) |
 *
 * 네이티브 쪽은 적어 둘 것이 없다 — 토큰의 진실은 셸이 들고 있고, 부팅마다 물어
 * 서버에 다시 실어 보내면 그것이 곧 갱신이다. 서버는 같은 값이면 덮어써도 무해하고,
 * 옛 토큰이 남아 있었다면 이 등록이 그것을 대체한다.
 */
async function reconcilePushMeans(
  runtime: BloomRuntimeConfig,
  sdk: Tv2mSdk,
  push: WebPushAdapter,
  native: NativeBridge | null,
): Promise<ReconciledMeans> {
  if (native !== null) {
    // **권한을 요청하지 않는다** (WB-07). 이미 허용된 기기의 토큰만 받아 온다 —
    // 그렇지 않으면 `null` 이고, 설정 시트의 [알림 켜기] 가 맡는다.
    const asking = native.getPushRegistration();
    const settled = await settleWithin(asking, MEANS_TIMEOUT_MS, null, setTimeout);
    return {
      means: settled.value === null ? null : nativeMeans(settled.value),
      // 넘겼으면 원래 약속을 호출부에 넘긴다. **취소하지 않는다** — 토큰이 늦게라도
      // 오면 그것으로 다시 등록하는 것이 이 상한의 값어치 전부다.
      late: settled.timedOut ? asking.then((r) => (r === null ? null : nativeMeans(r))) : null,
      nativePush: settled.timedOut ? 'timeout' : settled.value === null ? 'missing' : 'ready',
    };
  }

  /*
   * 웹 경로에도 같은 상한을 씌운다. **이쪽은 알려진 무한 대기가 없다** —
   * `WebPushAdapter.getSubscription()` 이 `isSupported()` 로 먼저 걸러 내고, 통과해도
   * 부르는 것은 `serviceWorker.register()` 라 resolve 하거나 reject 한다
   * (`serviceWorker.ready` 는 이 앱에서 `DebugPage` 한 곳뿐이고 부팅 경로가 아니다).
   *
   * **그래도 씌우는 것은 부팅이 끝나는 것을 계약으로 만들기 위해서다.** 여기 새 대기가
   * 생기는 날 증상은 「그 기기만 알림이 안 온다」 하나이고, 그것을 앞의 실기가 이미
   * 한 번 치렀다.
   *
   * **늦은 값을 넘기지는 않는다** — 그 대기가 안 풀렸다는 것은 구독 자체가 없다는
   * 뜻이라 나중에 실어 보낼 것이 생기지 않는다.
   */
  const settled = await settleWithin(
    reconcileSubscription(runtime, sdk, push),
    MEANS_TIMEOUT_MS,
    null,
    setTimeout,
  );
  const verified = settled.value;
  return {
    means:
      verified === null
        ? null
        : { kind: 'webpush', subscription: verified.subscription, subscribedWithKey: verified.key },
    late: null,
    nativePush: null,
  };
}

/** 어느 키로 만들어졌는지 **아는** 구독. 모르는 구독은 여기까지 오지 못한다 */
interface VerifiedSubscription {
  subscription: WebPushSubscription;
  key: string;
}

/**
 * 부팅 때 구독을 현재 설정과 맞춘다 (WB-15·16, 절대 규칙 19).
 *
 * ── 이것이 없으면 무엇이 조용히 깨지는가 ──────────────────────
 *
 * VAPID 를 갈거나 모드를 바꾸면 기존 구독은 **다른 키로 만들어진 채 남는다.** 권한은
 * `granted` 이고 구독 객체도 멀쩡하며 화면은 「알림 켜짐」인데, 발송만 401/403 으로
 * 실패한다 — 시청자에게 남는 것은 「알림이 안 온다」 하나다.
 *
 * 판정은 순수 함수(`planReconcile`)가 하고 여기서는 그 결정을 실행만 한다.
 *
 * **권한을 요청하지 않는다** (WB-07). 기동은 사용자 제스처 밖이므로 이미 허용된 기기의
 * 구독만 손댄다. 그렇지 않은 기기는 설정 시트의 [알림 켜기] 가 맡는다.
 */
async function reconcileSubscription(
  runtime: BloomRuntimeConfig,
  sdk: Tv2mSdk,
  push: WebPushAdapter,
): Promise<VerifiedSubscription | null> {
  const stored = readSubscribedKey();
  const existing = await currentSubscription(push);
  const plan = planReconcile({
    mode: runtime.mode,
    vapidPublicKey: runtime.vapidPublicKey,
    permission: push.getPermission(),
    supported: push.isSupported(),
    hasSubscription: existing !== null,
    stored,
  });

  if (plan.kind === 'skip') return null;

  if (plan.kind === 'none') {
    // 브라우저가 구독을 버렸다. 적어 둔 것도 함께 버린다 — 남겨 두면 다음 부팅이
    // 있지도 않은 구독을 「키가 맞다」고 판정한다.
    clearSubscribedKey();
    return null;
  }

  const owner = subscriptionOwner(runtime.mode);

  if (plan.kind === 'reuse') {
    return existing === null ? null : { subscription: existing, key: runtime.vapidPublicKey };
  }

  /*
   * 재구독. 순서가 있다.
   *
   * 1. **이전 주인에게서 먼저 뗀다** (WB-16). 나중에 하면 그 사이에 그 서버가 죽은
   *    구독으로 계속 보낸다. 실패해도 진행한다 — 그쪽 정리가 이쪽 구독을 막을 이유가 없다.
   * 2. 브라우저 구독을 지운다. 안 지우면 `subscribe()` 가 **기존 것을 그대로 돌려준다**
   *    (`WebPushAdapter` — "이미 있으면 그것을 쓴다"). 그러면 키가 그대로다.
   * 3. 새 키로 만든다.
   */
  if (plan.unregisterFrom !== null) {
    try {
      if (plan.unregisterFrom === 'fast') {
        await unsubscribeBloomDevice({ serverUrl: config.serverUrl, deviceId: sdk.getDeviceId() });
      } else {
        await sdk.unregisterPushSubscription();
      }
    } catch {
      /* 이전 주인 정리 실패. 새 구독을 막을 이유가 아니다 */
    }
  }

  try {
    await push.unsubscribe();
    const created = await push.subscribe(runtime.vapidPublicKey);
    writeSubscribedKey(runtime.vapidPublicKey, owner);

    // 위탁형은 TV2M 에 다시 붙여야 한다. 자가발송형은 아래 `registerWithBloom` 가 싣는다.
    if (owner === 'tv2m') await sdk.registerPushSubscription();

    return { subscription: created, key: runtime.vapidPublicKey };
  } catch {
    // 새 구독을 만들지 못했다. **적어 둔 것을 지운다** — 지금 브라우저에 무엇이 남아
    // 있는지 알 수 없고, 모르는 것을 안다고 적어 두면 다음 부팅이 그것을 믿는다.
    clearSubscribedKey();
    return null;
  }
}

async function currentSubscription(push: WebPushAdapter): Promise<WebPushSubscription | null> {
  if (!push.isSupported() || push.getPermission() !== 'granted') return null;
  try {
    return await push.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Deferred 응답을 Deep Link 컨텍스트 모양으로 옮긴다.
 *
 * 두 경로가 같은 화면을 그린다 — QR 로 설치하고 돌아온 사람과 링크를 직접 누른 사람이
 * 다른 화면을 볼 이유가 없다. `found` 가 아니면 `null` 이고, 그때 화면은 홈이다.
 */
function contextFromDeferred(result: DeferredLinkResult | null): Tv2mLinkContext | null {
  if (!result?.found || !result.productId || !result.campaignId) return null;
  const context: Tv2mLinkContext = {
    productId: result.productId,
    campaignId: result.campaignId,
    source: result.source ?? 'tv2m_deferred',
    triggerType: result.triggerType ?? 'channel_content',
  };
  if (result.matchId) context.matchId = result.matchId;
  if (result.couponCode) context.coupon = result.couponCode;
  return context;
}
